import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { VoyageService } from '../../services/voyageService';
import { ToastService } from '../../services/toastService';
import { convertUTCToLocalForInput, convertLocalToUTC } from '../../utils/dateUtils';
import type {
  StandardizedVoyageWithVessel,
  StandardizedVoyageEditForm,
  StandardizedVoyageUpdateRequest,
} from '@/types';

interface StandardizedVoyageEditDialogProps {
  open: boolean;
  onClose: () => void;
  voyage: StandardizedVoyageWithVessel | null;
}

export const StandardizedVoyageEditDialog = ({
  open,
  onClose,
  voyage,
}: StandardizedVoyageEditDialogProps) => {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<StandardizedVoyageEditForm>({
    defaultValues: {
      portDepartureName: '',
      portArrivalName: '',
      atd: '',
      ata: '',
      editedEUEmissionsCO2: undefined,
    },
  });

  const updateVoyageMutation = useMutation({
    mutationFn: (data: {
      voyageId: string;
      vesselId: string;
      updates: StandardizedVoyageUpdateRequest;
    }) => VoyageService.updateStandardizedVoyage(data.voyageId, data.vesselId, data.updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['voyages'] });
      ToastService.success('Voyage updated successfully');
      onClose();
    },
    onError: (error) => {
      console.error('Failed to update voyage:', error);
      ToastService.handleApiError(error, 'Updating Voyage');
    },
  });

  // Reset form when voyage changes
  useEffect(() => {
    if (voyage && open) {
      reset({
        portDepartureName: voyage.portDepartureName,
        portArrivalName: voyage.portArrivalName,
        atd: convertUTCToLocalForInput(voyage.atd),
        ata: convertUTCToLocalForInput(voyage.ata),
        editedEUEmissionsCO2: voyage.editedEUEmissionsCO2,
      });
    }
  }, [voyage, open, reset]);

  const handleEditVoyage = useCallback(
    (data: StandardizedVoyageEditForm) => {
      if (!voyage || !voyage.vesselID) return;

      const updates: StandardizedVoyageUpdateRequest = {
        portDepartureName: data.portDepartureName,
        portArrivalName: data.portArrivalName,
        atd: convertLocalToUTC(data.atd),
        ata: convertLocalToUTC(data.ata),
        editedEUEmissionsCO2: data.editedEUEmissionsCO2,
      };

      updateVoyageMutation.mutate({
        voyageId: voyage.voyageID,
        vesselId: voyage.vesselID,
        updates,
      });
    },
    [voyage, updateVoyageMutation]
  );

  const handleClose = useCallback(() => {
    if (!updateVoyageMutation.isPending) {
      reset();
      onClose();
    }
  }, [updateVoyageMutation.isPending, reset, onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit(handleEditVoyage)}>
        <DialogTitle>Edit Voyage</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            <TextField
              label="Departure Port"
              {...register('portDepartureName', {
                required: 'Departure port is required',
                minLength: { value: 2, message: 'Port name must be at least 2 characters' },
              })}
              error={!!errors.portDepartureName}
              helperText={errors.portDepartureName?.message}
              fullWidth
            />

            <TextField
              label="Arrival Port"
              {...register('portArrivalName', {
                required: 'Arrival port is required',
                minLength: { value: 2, message: 'Port name must be at least 2 characters' },
              })}
              error={!!errors.portArrivalName}
              helperText={errors.portArrivalName?.message}
              fullWidth
            />

            <TextField
              label="Actual Time of Departure (Local Time)"
              type="datetime-local"
              {...register('atd', {
                required: 'Departure time is required',
                validate: (value) => {
                  if (!value) return 'Departure time is required';
                  const date = new Date(value);
                  if (isNaN(date.getTime())) {
                    return 'Please enter a valid date and time';
                  }
                  return true;
                },
              })}
              error={!!errors.atd}
              helperText={errors.atd?.message || 'Enter time in your local timezone'}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <TextField
              label="Actual Time of Arrival (Local Time)"
              type="datetime-local"
              {...register('ata', {
                required: 'Arrival time is required',
                validate: (value, formValues) => {
                  if (!value) return 'Arrival time is required';
                  const arrivalDate = new Date(value);
                  if (isNaN(arrivalDate.getTime())) {
                    return 'Please enter a valid date and time';
                  }
                  if (formValues.atd && value) {
                    const departureDate = new Date(formValues.atd);
                    if (arrivalDate <= departureDate) {
                      return 'Arrival time must be after departure time';
                    }
                  }
                  return true;
                },
              })}
              error={!!errors.ata}
              helperText={errors.ata?.message || 'Enter time in your local timezone'}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <TextField
              label="Edited EU Emissions CO2 (tons)"
              type="number"
              {...register('editedEUEmissionsCO2', {
                setValueAs: (value) => {
                  if (value === '' || value == null || value === undefined) return undefined;
                  const num = parseFloat(value);
                  return isNaN(num) ? undefined : num;
                },
                min: { value: 0, message: 'CO2 emissions cannot be negative' },
                max: { value: 100000, message: 'CO2 emissions seems too high' },
                validate: (value) => {
                  if (value === undefined || value === null) return true;
                  const num = Number(value);
                  if (isNaN(num)) {
                    return 'Please enter a valid number';
                  }
                  if (num < 0) {
                    return 'CO2 emissions cannot be negative';
                  }
                  if (num > 100000) {
                    return 'CO2 emissions seems too high';
                  }
                  return true;
                },
              })}
              error={!!errors.editedEUEmissionsCO2}
              helperText={errors.editedEUEmissionsCO2?.message}
              fullWidth
              slotProps={{
                input: {
                  inputProps: {
                    step: 'any',
                    min: 0,
                    max: 100000,
                  },
                },
              }}
            />
          </Box>

          {/* Display read-only voyage information */}
          {voyage && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Box sx={{ mb: 2, pb: 1, borderBottom: '1px solid', borderColor: 'grey.300' }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Voyage Information (Read-Only)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  The following information is for reference only and cannot be modified
                </Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField
                  label="Voyage ID"
                  value={voyage.voyageID}
                  slotProps={{ input: { readOnly: true } }}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Voyage Number"
                  value={voyage.voyageNumber}
                  slotProps={{ input: { readOnly: true } }}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Vessel Name"
                  value={voyage.vesselName}
                  slotProps={{ input: { readOnly: true } }}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="IMO"
                  value={voyage.imo}
                  slotProps={{ input: { readOnly: true } }}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Original EU Emissions CO2"
                  value={`${voyage.euEmissionsCO2} tons`}
                  slotProps={{ input: { readOnly: true } }}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Data Type"
                  value={voyage.dataType === 'actual' ? 'Actual' : 'Estimate'}
                  slotProps={{ input: { readOnly: true } }}
                  fullWidth
                  size="small"
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={updateVoyageMutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={updateVoyageMutation.isPending}>
            {updateVoyageMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
